/**
 * Eingabe eines einzelnen Emojis über den Picker (statt Freitext): Knopf mit aktuellem Emoji, Leeren-Knopf.
 * Ergebnis als Zeichenkette (siehe lib/emojiString).
 */
import { SmilePlus, X } from 'lucide-react';
import { useState } from 'react';
import { emojiToString, type EmojiStringFormat } from '../lib/emojiString';
import { EmojiPickerPopover } from './EmojiPicker';
import { EmojiText } from './EmojiText';
import { anchorFrom, type Anchor } from './Popover';
import './emoji.css';

interface Props {
  value: string | null;
  onChange: (value: string | null) => void;
  /** Barrierefreie Beschriftung, z. B. „Status-Emoji". */
  label: string;
  testId: string;
  serverId?: string | null;
  unicodeOnly?: boolean;
  format?: EmojiStringFormat;
  disabled?: boolean;
  id?: string;
}

export function EmojiField({ value, onChange, label, testId, serverId, unicodeOnly, format = 'markup', disabled, id }: Props) {
  const [anchor, setAnchor] = useState<Anchor | null>(null);
  return (
    <span className="emoji-field" data-testid={testId} data-value={value ?? ''}>
      <button
        id={id}
        type="button"
        className="emoji-field-btn"
        aria-label={value ? `${label}: ${value} – ändern` : `${label} auswählen`}
        aria-haspopup="dialog"
        aria-expanded={!!anchor}
        disabled={disabled}
        onClick={(e) => setAnchor(anchorFrom(e.currentTarget))}
        data-testid={`${testId}-button`}
      >
        {value ? <EmojiText value={value} size={22} /> : <SmilePlus size={20} aria-hidden="true" />}
      </button>
      {value && !disabled && (
        <button type="button" className="icon-btn emoji-field-clear" aria-label={`${label} entfernen`} onClick={() => onChange(null)} data-testid={`${testId}-clear`}>
          <X size={14} />
        </button>
      )}
      {anchor && (
        <EmojiPickerPopover
          anchor={anchor}
          onClose={() => setAnchor(null)}
          serverId={serverId}
          unicodeOnly={unicodeOnly}
          title={label}
          testId={`${testId}-picker`}
          onSelect={(e) => onChange(emojiToString(e, format))}
        />
      )}
    </span>
  );
}
